<template>
  <form id="step6" @submit="sendForm">
    <h3 v-if="this.$i18n.locale === 'tr'" class="main_question text-center font-weight-normal">
      {{ $t('Nasıl ödeme yapabilirim?') }}
      <a
        id="howToPay"
        data-target="#how-to-pay-modal"
        data-toggle="modal"
        href="#"
        class="btn-outline-mukellef btn-radius mx-2"
      >
        <i class="fa fa-play link-button-icon"></i><span>{{ $t('Videoyu İzleyin') }}</span>
      </a>
    </h3>
    <h3 class="main_question"><strong>3/3</strong>{{ $t('Ödeme') }}</h3>
    <div class="card-input">
      <div class="row" v-for="(price, index) in prices" :key="index">
        <div class="col-6">
          <div class="form-group">{{ getProductName(price) }}</div>
        </div>
        <div class="col-6 text-right">
          <div class="form-group">
            <b v-if="price.net_price !== 0"
              >{{ formatPrice(Number((price.net_price * 100) / (100 + Number(price.vat_rate))), price.currency) }}+KDV{{
                price.type === 'subscription' ? (price.period === 'annual' ? ' / ' + $t('Yıl') : ' / ' + $t('Ay')) : ''
              }}</b
            >
            <b v-else class="free-text">{{
              price.code === 'COMMITMENT' ? 'Tüm planlarda ücretsiz' : $t('Ücretsiz')
            }}</b>
          </div>
        </div>
      </div>
      <div v-if="discount" class="promotion-summary">
        <div class="row">
          <div class="col-6">
            <div class="form-group color-orange">{{ $t('İndirim Tutarı') }}</div>
          </div>
          <div class="col-6 text-right color-orange">
            <div class="form-group">
              -
              <b>{{ formatPrice(discount, '₺') }}</b>
            </div>
          </div>
        </div>
      </div>
      <div v-if="calculatedTotalUpfrontDiscount" class="promotion-summary">
        <div class="row">
          <div class="col-6">
            <div class="form-group color-orange">Peşin ödemede %5 indirim</div>
          </div>
          <div class="col-6 text-right color-orange">
            <div class="form-group">
              -
              <b>{{ formatPrice(calculatedTotalUpfrontDiscount, '₺') }}</b>
            </div>
          </div>
        </div>
      </div>
      <hr class="hr-custom" />
      <div class="row fade-price-text">
        <div class="col-6 mt-2">
          <div>Net Tutar</div>
        </div>
        <div class="col-6 text-right mt-2">
          <div>
            <span>{{ formatPrice(Number(subtotal - subtotalVat), '₺') }}</span>
          </div>
        </div>
        <div class="col-6 mt-2">
          <div>KDV</div>
        </div>
        <div class="col-6 text-right mt-2">
          <div>
            <span>{{ formatPrice(Number(subtotalVat), '₺') }}</span>
          </div>
        </div>
        <template v-if="appliedAmount > 0">
          <div class="col-6 mt-2 color-orange">
            <div>Kredi İndirimi</div>
          </div>
          <div class="col-6 text-right mt-2 color-orange">
            <div>
              <span>{{ formatPrice(Number(appliedAmount), '₺') }}</span>
            </div>
          </div>
        </template>
      </div>
      <div class="row">
        <div class="col-6 mt-2 mb-2">
          <div><b>Toplam Tutar</b></div>
        </div>
        <div class="col-6 text-right mt-2 mb-2">
          <div>
            <b>{{ formatPrice(Number(subtotal - calculatedTotalUpfrontDiscount), '₺') }}</b>
          </div>
        </div>
      </div>

      <Promotion
        ref="promotion"
        :companyType="companyType"
        :form="form"
        :companyId="company.id"
        :companyNationId="company.company_nation_id"
        :discount.sync="discount"
        @update-products="updatePrices"
      />

      <Affiliate id="upgrade" ref="affiliate" :products="prices" @set-appliable-amount="setAppliableAmount" />

      <div>
        <CardSelection :selectedCard.sync="selectedCard" />
      </div>

      <div>
        <div v-if="selectedCard.id === 'other'">
          <div
            v-if="
              company.period === 'annual' &&
              !isFetchedInstallments &&
              !installmentOptions.length &&
              !discount &&
              !form.promotion_code.length
            "
            class="alert-mukellef-warning d-flex mt-3"
          >
            <i class="fa fa-info-circle mr-2 alert-icon-size alert-icon-spacing"></i>
            <div>Taksit seçeneklerini görüntüleyebilmek için lütfen kart bilgilerinizi giriniz.</div>
          </div>
          <h3 class="main_question">{{ $t('Kart Bilgileri') }}</h3>
          <div class="form-group" lang="tr">
            <InputField
              v-model="form.holder_name"
              name="credit-card-holder-name"
              :placeholder="$t('Kart üzerindeki isim')"
              type="text"
              v-validate="'required'"
              :error="errors.first('credit-card-holder-name')"
              :validateFields="fields['credit-card-holder-name']"
            />
            <error-box field="holder_name" />
          </div>
          <div class="form-group">
            <InputField
              v-model="form.number"
              v-mask="'#### #### #### ####'"
              name="credit-card-number"
              :placeholder="$t('Kart Numaranız')"
              :maxlength="19"
              type="text"
              @blur="cardMessage"
              v-validate="'required|check_card'"
              :error="errors.first('credit-card-number')"
              :validateFields="fields['credit-card-number']"
            />
            <error-box field="number" />
          </div>
          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <InputField
                  v-model="form.expire"
                  v-mask="'##/##'"
                  name="credit-card-expire-date"
                  :placeholder="$t('Son Kullanım Tarihi (aa/yy)')"
                  type="text"
                  v-validate="'required|check_expiry_date'"
                  :error="errors.first('credit-card-expire-date')"
                  :validateFields="fields['credit-card-expire-date']"
                />
                <error-box field="expire_year" />
                <error-box field="expire_month" />
              </div>
            </div>
            <div class="col-6">
              <div class="form-group">
                <InputField
                  v-model="form.cvc"
                  v-mask="'####'"
                  name="credit-card-cvv"
                  :placeholder="$t('CVV - Arka yüzdeki 3 hane')"
                  type="text"
                  v-validate="'required|min:3|max:4'"
                  :error="errors.first('credit-card-cvv')"
                  :validateFields="fields['credit-card-cvv']"
                />
                <error-box field="cvc" />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="
            installmentOptions.length && company.period === 'annual' && this.isShowInstallment && !this.appliedAmount
          "
          class="col-12 mb-4"
        >
          <div class="form-group">
            <label class="container_radio version_3 d-flex justify-content-between align-items-center">
              <div>
                Peşin öde
                <div v-if="totalUpfrontDiscount > 0" class="badge-recommended">%5 indirim</div>
                <input
                  v-model="paymentMethod"
                  class="required"
                  name="installment_option"
                  type="radio"
                  value="cash"
                  @change="PaymentMethodChanged(0)"
                />
                <span class="checkmark"></span>
              </div>
              <div>
                <div class="text-right">
                  <div class="mb-0 installment-big-text">
                    <div v-if="totalUpfrontDiscount > 0">
                      <span class="price-text overline">{{ formatPrice(Number(subtotal), '₺') }}</span>
                      <span class="price-text ml-1">{{
                        formatPrice(Number(subtotal - totalUpfrontDiscount), '₺')
                      }}</span>
                    </div>
                    <div v-else>
                      <span class="price-text">{{ formatPrice(Number(subtotal), '₺') }}</span>
                    </div>
                  </div>
                  <p class="mb-0 installment-small-text">*KDV dahildir.</p>
                </div>
              </div>
            </label>
          </div>
          <div>
            <a
              @click="toggleInstallment()"
              id="installment"
              class="text-dark"
              data-toggle="collapse"
              href="#installmentOptions"
              role="button"
              aria-expanded="false"
              aria-controls="installmentOptions"
            >
              Taksit Seçenekleri
              <div class="arrow-field d-inline-block">
                <span class="arrow" :class="isInstallmentOpened ? 'active' : ''">
                  <span></span>
                  <span></span>
                </span>
              </div>
              <span class="checkmark"></span>
            </a>
          </div>
          <div class="collapse" id="installmentOptions">
            <div v-for="(option, index) in installmentOptions" :key="index" class="installment-options">
              <label class="container_radio version_3 d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  {{ option.installment_number }} Taksit
                  <div
                    v-if="isAfterMay && formatPrice(option.total_price) == formatPrice(Number(subtotal))"
                    class="badge-recommended"
                  >
                    {{ $t('Vade farkı ödemeyin') }}
                  </div>
                  <input
                    v-model="paymentMethod"
                    class="required"
                    name="installment_option"
                    type="radio"
                    :value="option.installment_number"
                    @change="PaymentMethodChanged(option.total_price)"
                  />
                  <span class="checkmark"></span>
                </div>

                <div class="text-right">
                  <p class="mb-0 installment-big-text">
                    <span class="price-text">₺{{ option.installment_price }} / </span>
                    <span class="period-text">Ay</span>
                  </p>
                  <p class="mb-0 installment-small-text">Toplam: {{ formatPrice(Number(option.total_price), '₺') }}</p>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div>
          <div class="form-group terms">
            <label :class="{ 'is-invalid': errors.first('commit') }" class="container_check version_2">
              <span v-html="$t('Ödeme Kabul Text', { link: this.privacyLink })"></span>
              <input
                v-model="form.commit"
                name="commit"
                type="checkbox"
                value="false"
                v-validate="'required'"
                :error="errors.first('commit')"
                class="required up"
              />
              <span class="checkmark mt-2"></span>
            </label>
            <span v-show="errors.first('commit')" class="show-invalid invalid-feedback">{{
              errors.first('commit')
            }}</span>
          </div>
        </div>

        <div v-if="this.$i18n.locale === 'tr'">
          <label
            :class="{ 'is-invalid': errors.first('agreement-privacy') }"
            class="container_check version_2 mt-2"
            name="ortak"
            style="padding-top: 8px"
          >
            <i18n path="home.terms_of_use.small_text" tag="span">
              <template v-slot:privacy_text>
                <a data-target="#privacy-global-modal" data-toggle="modal" href="#">{{
                  $t('home.terms_of_use.privacy_text')
                }}</a>
              </template>
            </i18n>
            <input
              v-model="commitPrivacy"
              type="checkbox"
              value="false"
              class="required up"
              id="agreement-privacy"
              name="agreement-privacy"
              v-validate="'required'"
            />
            <span class="checkmark"></span>
          </label>
          <span v-show="errors.first('agreement-privacy')" class="show-invalid invalid-feedback">{{
            errors.first('agreement-privacy')
          }}</span>
        </div>
      </div>
    </div>
    <a v-show="false" id="creditCardMessage" data-target="#accepted-credit-card-modal" data-toggle="modal" href="#" />
    <IyzicoAgreementModal />
    <FreePromotionAppliedModal />
    <AcceptedCreditCardModal />
    <PrivacyGlobalModal v-if="this.$i18n.locale === 'tr'" :is-customer="true" />
  </form>
</template>
<script>
import IyzicoAgreementModal from '@/components/modals/policies/IyzicoModal';
import FreePromotionAppliedModal from '@/components/modals/FreePromotionAppliedModal';
import AcceptedCreditCardModal from '@/components/modals/AcceptedCreditCardModal';
import ProductStore from '@/stores/product';
import CompanyStore from '@/stores/company';
import PaymentStore from '@/stores/payment';
import InputField from '@/components/InputField.vue';
import Promotion from '@/components/Promotion';
import PrivacyGlobalModal from '@/components/modals/policies/PrivacyGlobalModal';
import CardSelection from '@/components/CardSelection';

export default {
  computed: {
    isAfterMay() {
      const startDate = new Date('2024-05-31T23:59:59');
      const currentDate = new Date();

      return currentDate >= startDate;
    },
    subtotalWithInstallment: function () {
      let finalPrice = 0;
      if (this.paymentMethod !== 'cash') {
        finalPrice = this.installmentTotalPrice;
        return finalPrice - this.calculatedTotalUpfrontDiscount;
      } else {
        return this.subtotal - this.calculatedTotalUpfrontDiscount;
      }
    },

    subtotal: function () {
      let finalPrice = 0;

      if (this.prices.length > 0) {
        finalPrice = this.prices.reduce((total, price) => total + (price.net_price - price.discount), 0);
      }
      return finalPrice - this.appliedAmount;
    },
    calculatedTotalUpfrontDiscount: function () {
      const discount = 0;

      if (this.paymentMethod !== 'cash') {
        return discount;
      }

      return this.paymentMethod === 'cash' ? this.totalUpfrontDiscount : 0;
    },
    totalUpfrontDiscount: function () {
      let discount = 0;

      if (this.prices.length > 0) {
        discount = this.prices.reduce(
          (total, price) =>
            total + (price.has_single_payment_discount && !price.discount ? price.single_payment_discount : 0),
          0
        );
      }
      return discount;
    },
    subtotalVat() {
      let totalVat = 0;

      if (this.prices.length > 0) {
        totalVat = this.prices.reduce(
          (total, price) =>
            total +
            (Number(price.final_price) -
              (price.has_single_payment_discount && this.paymentMethod === 'cash' && !price.discount
                ? price.single_payment_discount
                : 0)) *
              (Number(price.vat_rate) / (100 + Number(price.vat_rate))),
          0
        );
      }
      return totalVat;
    },
    expire_month: function () {
      return this.form.expire.split('/')[0];
    },
    expire_year: function () {
      return this.form.expire.split('/')[1];
    },
  },
  watch: {
    subtotalWithInstallment: function (val) {
      this.$emit('subtotal', { currency: '₺', price: val });

      if (val === 0) {
        document.getElementById('freeSubscription').click();
      }
    },
    discount: function (val) {
      if (val) {
        this.paymentMethod = 'cash';
        this.installmentOptions = [];
      }
    },
    'form.number': function (newVal, oldVal) {
      this.getInstallments();
    },
    selectedCard: function (newVal) {
      if (newVal.id !== 'other' || (newVal.id === 'other' && this.form.number)) {
        this.getInstallments();
      } else {
        this.installmentOptions = [];
      }
    },
  },
  data() {
    return {
      appliedAmount: 0,
      isLoading: false,
      acceptedCreditCardModalPassive: false,
      tooltipText: {
        content:
          '<b>İşletme türü şirketiniz için;</b> <br> Standard pakette <b>DKISLETME</b> koduyla 6 ay boyunca <b>99TL</b> indirim',
        classes: 'tooltip-inner-width',
      },
      commitPrivacy: false,
      rate: 1,
      prices: [],
      discount: 0,
      isShowInstallment: true,
      form: {
        product_id: [],
        expire: null,
        promotion_code: [],
        commit: false,
      },
      isHaveAdvancePackage: false,
      isHaveStandartPackage: false,
      products: [],
      package: [],
      companyType: {},
      company: {},
      workerProductsMap: {
        0: null,
        '1-3': 'EMPLOYEES3',
        '4-9': 'EMPLOYEES9',
        '10-50': 'EMPLOYEES50',
        '50+': 'EMPLOYEES50PLUS',
      },
      installmentTotalPrice: 0,
      paymentMethod: 'cash',
      isInstallmentOpened: false,
      installmentOptions: [],
      isFetchedInstallments: false,
      notVisibleProducts: ['VIRTUALOFFICESER', 'VIRTUALOFFICEST'],
      selectedCard: {
        id: 'other',
      },
    };
  },
  created() {
    this.startWaiting();
    $axios.get('company?include=companyType,users,users.channel').then((s) => {
      const company = s.data.data[0];
      this.company = company;
      this.virtualOffice = company.virtual_office;
      this.companyType = company.companyType.data;
      this.package = company.package;

      if (this.package.includes('ADVANCE_TRPACKAGE')) {
        this.isHaveAdvancePackage = true;
      } else if (this.package.includes('STANDART_TRPACKAGE')) {
        this.isHaveStandartPackage = true;
      }

      this.startWaiting();
      ProductStore.dispatch('getProducts', {
        company_type_id: company.company_type_id,
        appliablePromotionCode: 1,
      }).then((s) => {
        this.productsHandler(s);
      });
    });
  },
  methods: {
    setAppliableAmount(data) {
      this.appliedAmount = data;
      if (data) {
        this.paymentMethod = 'cash';
      }
    },
    checkIsShowInstallment() {
      const isValid = this.prices.every((obj) => {
        if (obj.appliedPromotionCode && !Array.isArray(obj.appliedPromotionCode.data)) {
          return obj.appliedPromotionCode.data.promotion.data.is_channel_promotion === true;
        }
        return true; // appliedPromotionCode yoksa veya data array değilse true dön
      });

      if (isValid) {
        this.isShowInstallment = true;
      } else {
        this.isShowInstallment = false;
      }
    },
    updatePrices(data) {
      this.paymentMethod = 'cash';
      this.$refs.affiliate.resetAmount();
      this.prices.forEach((product) => {
        const found = data.products.find((element) => product.code === element.code);
        if (found && found.net_price !== 0) {
          Object.assign(product, found);
        }
      });
      this.checkIsShowInstallment();
      this.getInstallments();
    },
    PaymentMethodChanged(price) {
      this.installmentTotalPrice = price;
    },
    getInstallments() {
      const number =
        this.form.number && this.selectedCard.id === 'other'
          ? this.form.number.replaceAll(' ', '')
          : this.selectedCard.installment_number;

      if (
        ((number && number.length >= 16) || (this.selectedCard.id && this.selectedCard.id !== 'other')) &&
        this.company.period === 'annual' &&
        this.isShowInstallment &&
        !this.discount
      ) {
        this.isLoading = true;
        PaymentStore.dispatch('getInstallments', {
          bin_number: number.slice(0, 6),
          price: Number(this.subtotal).toFixed(2),
        })
          .then((s) => {
            this.installmentOptions = s.data.data.installments;
            this.paymentMethod = 'cash';
          })
          .catch((e) => {
            this.installmentOptions = [];
            this.paymentMethod = 'cash';
          })
          .finally(() => {
            this.isFetchedInstallments = true;
            this.isLoading = false;
          });
      }
    },
    toggleInstallment() {
      const isHaveCollapse = document.getElementById('installmentOptions').classList.contains('collapse');

      const isHaveShow = document.getElementById('installmentOptions').classList.contains('show');

      if (isHaveCollapse) {
        this.isInstallmentOpened = true;
      }
      if (isHaveShow) {
        this.isInstallmentOpened = false;
      }
    },
    cardMessage(e) {
      if (!this.acceptedCreditCardModalPassive) {
        window.document.getElementById('creditCardMessage').click();
        this.acceptedCreditCardModalPassive = true;
      }
    },
    optional(variable) {
      if (variable) {
        return variable;
      }

      return {};
    },
    async productsHandler(s) {
      if (this.isHaveAdvancePackage) {
        this.optional(this.product('VIRTUALOFFICESER', s.data.data)).net_price = 0;
        this.optional(this.product('VIRTUALOFFICESER', s.data.data)).final_price = 0;
        this.optional(this.product('VIRTUALOFFICESER', s.data.data)).useable_wallet_amount = 0;

        this.optional(this.product('VIRTUALOFFICEST', s.data.data)).net_price = 0;
        this.optional(this.product('VIRTUALOFFICEST', s.data.data)).final_price = 0;
        this.optional(this.product('VIRTUALOFFICEST', s.data.data)).useable_wallet_amount = 0;
      }
      this.products = s.data.data;

      this.package.forEach((pack) => {
        const product = this.optional(this.product(pack, this.products));
        this.form.product_id.push(product.id);
        const isProductinNotVisibleProducts = this.notVisibleProducts.includes(product.code);
        if (!isProductinNotVisibleProducts || (isProductinNotVisibleProducts && product.final_price !== 0)) {
          this.prices.push(product);
        }
      });

      const commitmentProduct = this.optional(this.product('COMMITMENT', this.products));

      if (this.company.package === null || !this.company.package.includes('COMMITMENT')) {
        commitmentProduct.net_price = 0;
        commitmentProduct.final_price = 0;
        commitmentProduct.useable_wallet_amount = 0;
        this.prices.push(commitmentProduct);
      }

      if (this.company.employee_count) {
        const employeeProductCode = this.workerProductsMap[this.company.employee_count];
        if (employeeProductCode) {
          const employeeProduct = this.product(employeeProductCode, this.products);

          if (employeeProduct) {
            this.form.product_id.push(this.optional(employeeProduct).id);
            this.prices.push(employeeProduct);
          }
        }
      }
      /* this.prices[2].has_single_payment_discount = true;
      this.prices[2].single_payment_discount = 200; */

      const appliablePromotions = this.products.reduce(function (result, product) {
        if (product.appliable_promotion_code) {
          result.push(product.appliable_promotion_code);
        }
        return result;
      }, []);
      if (appliablePromotions.length) {
        await this.$refs.promotion.$refs.promotionModal.applyCode(appliablePromotions);
      }

      this.checkAndShowErrorMessage(Number(this.subtotalWithInstallment).toFixed(2), '₺');

      this.endWaiting();
    },
    product(code, products) {
      if (!products) {
        return null;
      }

      return products.find(function (product) {
        return product.code === code;
      });
    },
    sendForm(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const form = JSON.parse(JSON.stringify(this.form));
          if (this.selectedCard.id !== 'other') {
            delete form.expire_month;
            delete form.expire_year;
            delete form.number;
            delete form.expire;
            form.card_id = this.selectedCard.id;
          } else {
            form.expire_month = this.expire_month;
            form.expire_year = this.expire_year;
            form.number = this.form.number.replace(/ /g, '');
          }

          if (!form.promotion_code.length) {
            delete form.promotion_code;
          }
          if (
            this.paymentMethod !== 'cash' &&
            this.company.period === 'annual' &&
            this.installmentOptions.length &&
            this.isShowInstallment
          ) {
            form.installment = this.paymentMethod;
          }

          if (this.appliedAmount) {
            form.wallet_amount = this.appliedAmount;
          }
          if (this.isWebView) {
            form.is_mobile = true;
          }

          this.startWaiting();
          $axios.put('register/' + this.company.id + '/pay', form).then((s) => {
            document.write(s.data.data.html_3d);
            document.close();

            this.endWaiting();
          });
        }
      });
    },

    informHubspot() {
      CompanyStore.dispatch('informHubspot', {
        name: this.company.users.data[0].name,
        surname: this.company.users.data[0].surname,
        email: this.company.users.data[0].email,
      });
    },
    completed() {
      this.informHubspot();
      this.$toasted.show(this.$t('AlertMessages.Ödemeniz başarıyla tamamlandı'), {
        duration: 60000,
      });
      // Callback to parent component
      // eslint-disable-next-line node/no-callback-literal
      callback(true);
    },
  },
  components: {
    IyzicoAgreementModal,
    FreePromotionAppliedModal,
    AcceptedCreditCardModal,
    Promotion,
    PrivacyGlobalModal,
    InputField,
    CardSelection,
  },
};
</script>
<style lang="less" scoped>
.fade-price-text {
  color: #393939;
  font-size: 12px;
}
.badge-recommended {
  margin-left: 10px;
  display: inline-flex;
  padding: 2px 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 41px;
  border: 1px solid #00aa6b;
  background: rgba(60, 199, 134, 0.16);
  color: #00aa6b;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 166.667% */
  letter-spacing: -0.12px;
}
.arrow-field {
  width: 30px;
  transform: scale(0.6);
  text-align: center;
  border: black;
  .arrow {
    span {
      background: black;
    }
  }
}

.installment-options {
  padding: 16px 0px 6px 0px;
}

.installment-options:not(:last-of-type) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.installment-small-text {
  color: #4b5563;
  font-size: 10px;
  line-height: 24px;
}
.installment-big-text {
  font-size: 12px;
  line-height: 24px;
  .period-text {
    font-weight: 500;
  }
  .price-text {
    font-weight: 700;
  }
}

.container_radio.version_3 {
  padding: 0px 0 0 45px;
  min-height: 30px;
}
.container_radio.version_3 input:checked ~ .checkmark:before {
  opacity: 1;
}
.container_radio.version_3 .checkmark {
  position: absolute;
  top: 0px;
  bottom: 0px;
  margin: auto;
  left: 0;
  height: 30px;
  width: 30px;
  border: 1px solid #cccccc;
  border-radius: 50%;
}
.container_radio.version_3 .checkmark:after {
  width: 30px;
  height: 30px;
  top: -1px;
  left: -1px;
}
.container_radio.version_3 .checkmark:before {
  display: block;
  content: '';
  position: absolute;
  opacity: 0;
  -moz-transition: all 0.3s ease-in-out;
  -o-transition: all 0.3s ease-in-out;
  -webkit-transition: all 0.3s ease-in-out;
  -ms-transition: all 0.3s ease-in-out;
  transition: all 0.3s ease-in-out;
  left: 12px;
  top: 8px;
  width: 5px;
  height: 10px;
  border: solid white;
  z-index: 999;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
</style>
